import React, { useState, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { Grid } from "@material-ui/core";
import { LOAN_TYPES } from "@loanbossinc/component-library";
import { Formik } from "formik";
import { getSponsorCompanies } from "store/state/companies";
import AmortizationTypes from "constants/amortizationTypes";
import { getConfigItemByParentId } from "store/state/configItems";
import { CASHOUT_PROCEEDS_PREFERRED_CONSTRAINT } from "constants/configItemParents";
import PageWrapper from "components/Global/PageOrganization/PageWrapper";
import _ from "lodash";

import { Header, AssumptionsCard, SpreadCreditModule } from "..";

import {
  saveLoanProceedsLoanTypeSettingsAssumptions,
  saveLoanProceedsLoanTypeSettingsSpreadsAndConstraints,
  getLoanProceedsLoanTypeSettingsAssumptions,
  getLoanProceedsUnderwritingConstraints,
  getLoanProceedsSpreads,
  updateCreditAdjustments,
  cloneExistingSpreadAndRunValues
} from "./LoanProceedsSettings.store";
import "./styles.css";

export const SpreadCreditModuleType = {
  SPREAD: "Spread Matrices",
  CREDIT: "Credit Adjustments"
};

const { SPREAD, CREDIT } = SpreadCreditModuleType;

const LoanProceedsSettings = ({
  getConfigItemByParentId,
  configItems,
  authentication,
  saveLoanProceedsLoanTypeSettingsAssumptions,
  saveLoanProceedsLoanTypeSettingsSpreadsAndConstraints,
  getLoanProceedsLoanTypeSettingsAssumptions,
  loanProceedsAssumptions,
  loanProceedsConstraints,
  getLoanProceedsUnderwritingConstraints,
  getLoanProceedsSpreads,
  loanProceedsSpreads,
  updateCreditAdjustments,
  cloneExistingSpreadAndRunValues,
  getSponsorCompanies,
  companies
}) => {
  const { configItemByParent = [] } = configItems;
  const { companySponsorCompanies = [] } = companies;

  const [spreads, setSpreads] = useState([]);
  const [creditAdjustments, setCreditAdjustments] = useState([]);
  const [constraintsByLoanType, setConstraintsByLoanType] = useState([]);
  const [constraints, setConstraints] = useState(constraintsByLoanType);
  const [preferredConstraintsList, setPreferredConstraintsList] = useState([]);
  const [userCompanyId, setUserCompanyId] = useState(null);
  const [selectedSponsorCompanyId, setSelectedSponsorCompanyId] = useState(null);
  const [dropdownCompanies, setDropdownCompanies] = useState([]);
  const [selectedLoanType, setSelectedLoanType] = useState(LOAN_TYPES.BALANCE_SHEET);
  const [savedAssumptions, setSavedAssumptions] = useState({});
  const [spreadMatrixSaveDisabled, setSpreadMatrixSaveDisabled] = useState(true);
  const [creditAdjSaveDisabled, setCreditAdjustmentSaveDisabled] = useState(true);
  const [sponsorConstraintsByLoanType, setSponsorConstraintsByLoanType] = useState([]);
  const [spreadsByLoanTypeSettingId, setSpreadsByLoanTypeSettingId] = useState([]);
  const [sponsorSpreadsByLoanTypeSettingId, setSponsorSpreadsByLoanTypeSettingId] = useState([]);
  const [isBroker, setIsBroker] = useState(false);
  const [moduleType, setModuleType] = useState(SPREAD);
  const { sponsorCompanySpreads } = loanProceedsSpreads?.find(({ sponsorCompanySpreads }) => sponsorCompanySpreads) ?? [];
  const { sponsorCompanyConstraints } = loanProceedsConstraints?.find(({ sponsorCompanyConstraints }) => sponsorCompanyConstraints) ?? [];

  const initialValues = {
    lpsLoanType: "",
    lpsRateType: "",
    lpsRateIndex: "",
    lpsClosingCosts: "",
    lpsAmortizationType: "",
    lpsAmortizationTerm: "",
    lpsAmortizationTermForCashflows: "",
    lpsAmortizationRateForCashflows: "",
    lpsAmortizationRate: "",
    lpsIoPeriods: "",
    lpsDaycount: "",
    lpsConstraintPreferenceId: "",
    lpsConstraints: [],
    lpsUnderwritingRate: "",
    companyIdToClone: ""
  };

  useEffect(() => {
    const { id } = authentication?.user?.contact?.company ?? null;
    setUserCompanyId(id);
    getConfigItemByParentId({ id: CASHOUT_PROCEEDS_PREFERRED_CONSTRAINT });
    getSponsorCompanies();
  }, []);

  useEffect(() => {
    const broker = authentication?.companyTypes?.some(({ name }) => name === "Broker");
    setIsBroker(Boolean(broker));
    (Boolean(broker) && moduleType === CREDIT) ? setSelectedSponsorCompanyId(companySponsorCompanies?.[0]?.id) : setSelectedSponsorCompanyId(null);
  }, [authentication, moduleType]);

  useEffect(() => {
    getLoanProceedsSpreads({ sponsorCompanyId: selectedSponsorCompanyId });
    getLoanProceedsUnderwritingConstraints({ sponsorCompanyId: selectedSponsorCompanyId });
    if (selectedSponsorCompanyId) {
      setSponsorSpreadsByLoanTypeSettingId(sponsorCompanySpreads?.filter(({ loanProceedsLoanTypeSetting }) => loanProceedsLoanTypeSetting?.loanTypeId === selectedLoanType));
      setSponsorConstraintsByLoanType(sponsorCompanyConstraints?.filter(({ loanTypeId }) => loanTypeId === selectedLoanType));
    } else {
      setSpreadsByLoanTypeSettingId(loanProceedsSpreads.filter(({ loanTypeSettingId }) => loanTypeSettingId === savedAssumptions?.id));
      setConstraintsByLoanType(loanProceedsConstraints.filter(({ loanTypeId }) => loanTypeId === selectedLoanType));
    }
  }, [selectedSponsorCompanyId]);

  useEffect(() => {
    getLoanProceedsLoanTypeSettingsAssumptions({ loanTypeId: selectedLoanType });
    if (loanProceedsAssumptions?.loanTypeId !== selectedLoanType) {
      setSavedAssumptions({});
    }

    // For credit adjustments on broker companies" associated sponsor companies, we need to have a separate set of spreads and constraints to keep track of
    if (selectedSponsorCompanyId) {
      setSponsorSpreadsByLoanTypeSettingId(sponsorCompanySpreads?.filter(({ loanProceedsLoanTypeSetting }) => loanProceedsLoanTypeSetting?.loanTypeId === selectedLoanType));
      setSponsorConstraintsByLoanType(sponsorCompanyConstraints?.filter(({ loanTypeId }) => loanTypeId === selectedLoanType));
    } else {
      setSpreadsByLoanTypeSettingId(loanProceedsSpreads.filter(({ loanTypeSettingId }) => loanTypeSettingId === savedAssumptions?.id));
      setConstraintsByLoanType(loanProceedsConstraints.filter(({ loanTypeId }) => loanTypeId === selectedLoanType));
    }
  }, [selectedLoanType]);

  useEffect(() => {
    if (selectedSponsorCompanyId) {
      setSponsorConstraintsByLoanType(sponsorCompanyConstraints?.filter(({ loanTypeId }) => loanTypeId === selectedLoanType));
    }
    setConstraintsByLoanType(loanProceedsConstraints.filter(({ loanTypeId }) => loanTypeId === selectedLoanType));
  }, [loanProceedsConstraints]);

  useEffect(() => {
    const formattedList = configItemByParent[CASHOUT_PROCEEDS_PREFERRED_CONSTRAINT]?.map(({ id, value }) => ({ name: value, label: value, value: id }));
    const preferredConstraintListFormatted = formattedList ? [{ name: "None", label: "", value: "" }, ...formattedList] : [];
    setPreferredConstraintsList(preferredConstraintListFormatted);
  }, [configItems]);

  useEffect(() => {
    if (selectedSponsorCompanyId) {
      !loanProceedsSpreads[0]?.sponsorCompanySpreads?.length === 0 ?
        setSponsorSpreadsByLoanTypeSettingId(sponsorCompanySpreads?.filter(({ loanProceedsLoanTypeSetting }) => loanProceedsLoanTypeSetting?.loanTypeId === selectedLoanType)) :
        setSponsorSpreadsByLoanTypeSettingId([]);
    } else if (!selectedSponsorCompanyId) {
      setSpreadsByLoanTypeSettingId(loanProceedsSpreads[0]?.sponsorCompanySpreads?.length === 0 ? loanProceedsSpreads.filter(({ loanTypeSettingId }) => loanTypeSettingId === savedAssumptions?.id) : []);
    }
  }, [loanProceedsSpreads]);

  useEffect(() => {
    setSpreadMatrixSaveDisabled(savedAssumptions?.loanTypeId !== selectedLoanType);
    setCreditAdjustmentSaveDisabled(savedAssumptions?.loanTypeId !== selectedLoanType);
    if (_.isEmpty(savedAssumptions)) {
      setSpreadsByLoanTypeSettingId([]);
    } else {
      if (selectedSponsorCompanyId) {
        setSponsorSpreadsByLoanTypeSettingId(sponsorCompanySpreads?.filter(({ loanProceedsLoanTypeSetting }) => loanProceedsLoanTypeSetting?.loanTypeId === selectedLoanType));
      }
      setSpreadsByLoanTypeSettingId(loanProceedsSpreads.filter(({ loanTypeSettingId }) => loanTypeSettingId === savedAssumptions?.id));
    }
  }, [savedAssumptions, sponsorCompanySpreads]);

  useEffect(() => {
    setSavedAssumptions(loanProceedsAssumptions);
  }, [loanProceedsAssumptions]);

  const handleSubmission = values => {
    const assumptionsToSave = {
      companyId: userCompanyId,
      loanTypeId: selectedLoanType,
      amortTermMonths: values.lpsAmortizationTerm === "" ? null : values.lpsAmortizationTerm,
      amortRateIfFloating: values.lpsAmortizationRate === "" ? null : values.lpsAmortizationRate,
      rateTypeId: values.lpsRateType === "" ? null : values.lpsRateType,
      amortTypeId: values.lpsAmortizationType === "" ? null : values.lpsAmortizationType,
      amortTermMonthsForCashflows: values.lpsAmortizationTermForCashflows === "" ? null : values.lpsAmortizationTermForCashflows,
      amortRateIfFloatingForCashflows: values.lpsAmortizationRateForCashflows === "" ? null : values.lpsAmortizationRateForCashflows,
      ioPeriodsForCashflows: values.lpsAmortizationType === AmortizationTypes.INTEREST_ONLY || values.lpsIoPeriods === "" ? null : parseInt(values.lpsIoPeriods, 10),
      daycountId: values.lpsDaycount === "" ? null : values.lpsDaycount,
      closingCostsPercentage: values.lpsClosingCosts === "" ? null : parseFloat(values.lpsClosingCosts),
      rateIndexId: values.lpsRateIndex === "" ? null : values.lpsRateIndex,
      preferredConstraintId: values.lpsConstraintPreferenceId === "" ? null : values.lpsConstraintPreferenceId,
      uwRate: values.lpsUnderwritingRate === "" ? null : values.lpsUnderwritingRate
    };

    saveLoanProceedsLoanTypeSettingsAssumptions(assumptionsToSave);

    // TODO: make this a conditional based on successful post or not.
    setSpreadMatrixSaveDisabled(false);
    setCreditAdjustmentSaveDisabled(false);
  };

  const handleSaveSpreadMatrixValues = values => {
    const { lpsConstraints, lpsRateIndex } = values;

    const constraintsWithLoanType = lpsConstraints ? lpsConstraints.map(constraint => ({
      ...constraint,
      loanTypeId: selectedLoanType
    })) : [{ loanTypeId: selectedLoanType }];

    const spreadsWithRateIndexId = spreads.map(spread => ({
      ...spread,
      amount: spread.amount.map(a => ({
        ...a,
        spread: a.spread ? a.spread : 0
      })),
      rateIndexId: lpsRateIndex
    }));

    saveLoanProceedsLoanTypeSettingsSpreadsAndConstraints({
      spreads: spreadsWithRateIndexId,
      constraints: constraintsWithLoanType,
      loanTypeId: selectedLoanType,
      callback: () => {
        getLoanProceedsSpreads({ sponsorCompanyId: selectedSponsorCompanyId });
        getLoanProceedsUnderwritingConstraints({ sponsorCompanyId: selectedSponsorCompanyId });
      }
    });
  };

  const handleSaveCreditAdjustmentValues = () => {
    updateCreditAdjustments({ spreads, sponsorCompanyId: selectedSponsorCompanyId });
  };

  const handleResetForm = formikProps => {
    const { setFieldValue } = formikProps;

    setFieldValue("lpsRateIndex", savedAssumptions?.rateIndexId ?? "");
    setFieldValue("lpsAmortizationTerm", savedAssumptions?.amortTermMonths ?? "");
    setFieldValue("lpsAmortizationRate", savedAssumptions?.amortRateIfFloating ?? "");
    setFieldValue("lpsAmortizationType", savedAssumptions?.amortTypeId ?? "");
    setFieldValue("lpsClosingCosts", savedAssumptions?.closingCostsPercentage ?? "");
    setFieldValue("lpsRateType", savedAssumptions?.rateTypeId ?? "");
    setFieldValue("lpsAmortizationTermForCashflows", savedAssumptions?.amortTermMonthsForCashflows ?? "");
    setFieldValue("lpsAmortizationRateForCashflows", savedAssumptions?.amortRateIfFloatingForCashflows ?? "");
    setFieldValue("lpsIoPeriods", savedAssumptions?.ioPeriodsForCashflows ?? "");
    setFieldValue("lpsDaycount", savedAssumptions?.daycountId ?? "");
    setFieldValue("lpsConstraintPreferenceId", savedAssumptions?.preferredConstraintId ?? "");
    setFieldValue("lpsUnderwritingRate", savedAssumptions?.uwRate ?? "");
  };

  const handleCloneDefaultValuesClick = values => {
    cloneExistingSpreadAndRunValues({ existingCompanyId: values.companyIdToClone });
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={values => handleSubmission(values)}
      render={formikProps => (
        <div className="loan-proceeds-settings-wrapper">
          <PageWrapper>
            <Grid container direction="column" style={{ maxWidth: "1300px" }}>
              <Grid item>
                <Header
                  defaultValuesDisabled={formikProps.values?.companyIdToClone === ""}
                  handleClonePress={() => handleCloneDefaultValuesClick(formikProps.values)}
                  selectedLoanType={selectedLoanType}
                  setSelectedLoanType={val => setSelectedLoanType(val)}
                  {...formikProps}
                />
              </Grid>
              <Grid item style={{ padding: "2em 2em 2em 0" }}>
                <AssumptionsCard
                  onReset={() => handleResetForm(formikProps)}
                  preferredConstraintsList={preferredConstraintsList}
                  savedAssumptions={savedAssumptions}
                  {...formikProps}
                />
              </Grid>
              <Grid item style={{ padding: "2em 2em 2em 0" }}>
                <SpreadCreditModule
                  {...formikProps}
                  moduleType={moduleType}
                  setModuleType={setModuleType}
                  isBroker={isBroker}
                  loanProceedsSpreads={loanProceedsSpreads}
                  onSaveSpreads={() => handleSaveSpreadMatrixValues(formikProps.values)}
                  onSaveCreditAdjs={handleSaveCreditAdjustmentValues}
                  spreadMatrixSaveDisabled={spreadMatrixSaveDisabled}
                  creditAdjSaveDisabled={creditAdjSaveDisabled}
                  constraintsByLoanType={constraintsByLoanType}
                  sponsorConstraintsByLoanType={sponsorConstraintsByLoanType}
                  spreadsByLoanTypeSettingId={spreadsByLoanTypeSettingId}
                  sponsorSpreadsByLoanTypeSettingId={sponsorSpreadsByLoanTypeSettingId}
                  loanProceedsAssumptions={loanProceedsAssumptions}
                  savedAssumptions={savedAssumptions}
                  setSavedAssumptions={setSavedAssumptions}
                  creditAdjustments={creditAdjustments}
                  setCreditAdjustments={setCreditAdjustments}
                  spreads={spreads}
                  setSpreads={setSpreads}
                  constraints={constraints}
                  setConstraints={setConstraints}
                  selectedSponsorCompanyId={selectedSponsorCompanyId}
                  setSelectedSponsorCompanyId={setSelectedSponsorCompanyId}
                  selectedLoanType={selectedLoanType}
                  dropdownCompanies={dropdownCompanies}
                  setDropdownCompanies={setDropdownCompanies}
                />
              </Grid>
            </Grid>
          </PageWrapper>
        </div>
      )}
    />
  );
};

const mapStateToProps = ({ loanProceedsReducer: loanProceeds, configItems, authentication, companies }) => {
  const { loanProceedsAssumptions, loanProceedsConstraints, loanProceedsSpreads } = loanProceeds;

  return {
    loanProceeds,
    configItems,
    authentication,
    loanProceedsAssumptions,
    loanProceedsConstraints,
    loanProceedsSpreads,
    companies
  };
};

export default compose(connect(mapStateToProps, {
  getConfigItemByParentId,
  saveLoanProceedsLoanTypeSettingsAssumptions,
  getLoanProceedsLoanTypeSettingsAssumptions,
  saveLoanProceedsLoanTypeSettingsSpreadsAndConstraints,
  getLoanProceedsUnderwritingConstraints,
  getLoanProceedsSpreads,
  updateCreditAdjustments,
  getSponsorCompanies,
  cloneExistingSpreadAndRunValues
}))(LoanProceedsSettings);
